/**
 * FollowThroughRing — the trust signal on Today. A radial ring showing how well
 * you keep your commitments, with your streak alongside and a tap-to-expand
 * ledger of recently scored assignments.
 *
 * Made-vs-kept (rolling 30 days), computed client-side from the assignments the
 * dashboard already has (GET /assignments returns the full set with status /
 * issued_at / deadline / evaluation_score — no backend endpoint needed):
 *   - resolved = commitments that have come due (kept + missed)
 *   - kept   = completed & scored (status 'evaluated')
 *   - missed = pending past its deadline
 *   ('submitted' = delivered-but-not-yet-scored, and not-yet-due pending, are
 *    "in flight" — uncounted, neither kept nor missed yet)
 * The ring shows kept/resolved; if nothing has come due yet it falls back to the
 * daily completion average (daily_streaks.avg_completion_rate) so it's never
 * empty for an active user. Honest scope — no fabricated numbers.
 *
 * "Kept = evaluated" matches the weekly partner scorecard's definition. The ring
 * still won't match the scorecard exactly: it uses a 30-day window and a
 * came-due (kept+missed) denominator, vs the scorecard's 7-day window and raw
 * "made" denominator (all issued + session commitments). Different views, by design.
 */

import { useState, useEffect } from 'react';
import { Flame, ChevronDown } from 'lucide-react';

const SIZE = 76;
const STROKE = 7;
const R = (SIZE - STROKE) / 2;
const CIRC = 2 * Math.PI * R;
const WINDOW_DAYS = 30;

function toneFor(pct) {
  if (pct >= 80) return { stroke: '#22C55E', text: 'text-green-400' };
  if (pct >= 50) return { stroke: '#3B82F6', text: 'text-blue-400' };
  if (pct > 0) return { stroke: '#F59E0B', text: 'text-amber-400' };
  return { stroke: '#475569', text: 'text-slate-500' };
}

function Ring({ pct, label }) {
  // Animate the fill on mount (neutralized under prefers-reduced-motion).
  const [shown, setShown] = useState(0);
  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(pct));
    return () => cancelAnimationFrame(id);
  }, [pct]);

  const tone = toneFor(pct);
  const offset = CIRC * (1 - Math.max(0, Math.min(shown, 100)) / 100);

  return (
    <div className="relative flex-shrink-0" style={{ width: SIZE, height: SIZE }}>
      <svg width={SIZE} height={SIZE} className="-rotate-90" aria-hidden="true">
        <circle cx={SIZE / 2} cy={SIZE / 2} r={R} fill="none" stroke="rgb(51 65 85 / 0.7)" strokeWidth={STROKE} />
        <circle
          cx={SIZE / 2} cy={SIZE / 2} r={R} fill="none"
          stroke={tone.stroke} strokeWidth={STROKE} strokeLinecap="round"
          strokeDasharray={CIRC} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 700ms cubic-bezier(0.16, 1, 0.3, 1)' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-lg font-semibold leading-none ${tone.text}`}>{pct}%</span>
        <span className="text-[11px] text-slate-500 uppercase tracking-wide mt-0.5 leading-none">{label}</span>
      </div>
    </div>
  );
}

// Made-vs-kept over the rolling window, from the assignment set.
function computeFollowThrough(assignments) {
  const now = Date.now();
  const since = now - WINDOW_DAYS * 86400000;
  let kept = 0;
  let missed = 0;
  for (const a of Array.isArray(assignments) ? assignments : []) {
    if (!a || !a.issued_at) continue;
    const issued = new Date(a.issued_at).getTime();
    if (isNaN(issued) || issued < since) continue;
    if (a.status === 'evaluated') {
      kept++; // completed and scored — a verified kept commitment
    } else if (a.status === 'pending' && a.deadline && new Date(a.deadline).getTime() < now) {
      missed++; // overdue, not delivered
    }
    // 'submitted' (delivered, awaiting score) and not-yet-due 'pending' = in flight — uncounted.
  }
  const resolved = kept + missed;
  return { kept, missed, resolved, ratio: resolved > 0 ? Math.round((kept / resolved) * 100) : null };
}

export default function FollowThroughRing({ streak, assignments, className = '' }) {
  const [expanded, setExpanded] = useState(false);

  const dailyPct = Math.round(streak?.avg_completion_rate || 0);
  const current = streak?.current_streak || 0;
  const longest = streak?.longest_streak || 0;

  const ft = computeFollowThrough(assignments);
  const hasKept = ft.resolved >= 1;     // enough to state an honest count
  const ratioMode = ft.resolved >= 2;   // enough to headline a ratio (avoid n=1 0%/100% noise)
  const ringPct = ratioMode ? ft.ratio : dailyPct;
  const ringLabel = ratioMode ? 'kept' : 'avg';

  const evaluated = (Array.isArray(assignments) ? assignments : [])
    .filter(a => a && a.status === 'evaluated' && typeof a.evaluation_score === 'number' && a.evaluation_score > 0);
  const avgScore = evaluated.length
    ? Math.round((evaluated.reduce((s, a) => s + a.evaluation_score, 0) / evaluated.length) * 10) / 10
    : null;

  const ledger = [...evaluated]
    .sort((a, b) => new Date(b.evaluated_at || b.issued_at || 0) - new Date(a.evaluated_at || a.issued_at || 0))
    .slice(0, 6);
  const hasLedger = ledger.length > 0;

  const Wrapper = hasLedger ? 'button' : 'div';
  const ariaLabel = `Follow-through: `
    + (ratioMode ? `${ft.ratio}% of commitments kept, ${ft.kept} of ${ft.resolved}` : `${dailyPct}% average daily completion`)
    + (hasKept && !ratioMode ? `, ${ft.kept} of ${ft.resolved} commitments kept` : '')
    + (current > 0 ? `, ${current} day streak` : '')
    + `. ${expanded ? 'Hide' : 'Show'} scored assignments`;

  return (
    <div className={className}>
      <Wrapper
        {...(hasLedger ? { onClick: () => setExpanded(v => !v), 'aria-expanded': expanded, 'aria-label': ariaLabel } : {})}
        className={`w-full text-left rounded-lg border border-slate-700/40 bg-slate-800/40 shadow-soft p-4 flex items-center gap-4 ${hasLedger ? 'hover:bg-slate-800/60 transition-colors min-h-tap' : ''}`}
      >
        <Ring pct={ringPct} label={ringLabel} />

        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1.5">
            Follow-through{hasKept ? ' · 30 days' : ''}
          </p>
          <div className="flex items-center gap-1.5">
            <Flame size={15} aria-hidden="true" className={current > 0 ? 'text-orange-400 animate-flame-pulse' : 'text-slate-600'} />
            <span className={`text-sm font-medium ${current > 0 ? 'text-orange-300' : 'text-slate-500'}`}>
              {current > 0 ? `${current}-day streak` : 'No streak yet'}
            </span>
            {longest > 1 && current > 0 && <span className="text-[11px] text-slate-600">· best {longest}d</span>}
          </div>
          {hasKept && (
            <div className="flex items-center gap-1.5 mt-1">
              <span className="text-xs text-slate-400">
                <span className="text-slate-200 font-medium">{ft.kept} of {ft.resolved}</span> commitments kept
              </span>
              {ft.missed > 0 && <span className="text-[11px] text-amber-400">· {ft.missed} missed</span>}
            </div>
          )}
        </div>

        {hasLedger && (
          <ChevronDown size={16} aria-hidden="true" className={`text-slate-500 flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        )}
      </Wrapper>

      {expanded && hasLedger && (
        <div className="mt-2 rounded-lg border border-slate-700/40 bg-slate-900/40">
          {avgScore !== null && (
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/40">
              <span className="text-[11px] text-slate-500 uppercase tracking-wide">Scored assignments</span>
              <span className="text-xs text-slate-400">avg {avgScore}/5</span>
            </div>
          )}
          <div className="divide-y divide-slate-700/40">
            {ledger.map((a, i) => (
              <div key={a.id || i} className="flex items-center gap-3 px-3 py-2.5">
                <span className="text-sm text-slate-300 truncate flex-1 min-w-0">{a.goal_text}</span>
                <span className={`text-xs font-semibold flex-shrink-0 ${a.evaluation_score >= 4 ? 'text-green-400' : a.evaluation_score >= 3 ? 'text-amber-400' : 'text-slate-400'}`}>
                  {a.evaluation_score}/5
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
